import { z } from 'zod'

import { ConnectivityType } from '../lib/account-connectivity'

export const addAccountSchema = z
  .object({
    name: z.string().min(1, 'Description is required'),
    iban: z.string().optional(),
    accountNumber: z.string().optional(),
    institutionId: z.number({ required_error: 'Institution is required' }).refine((v) => v !== 0, 'Institution is required'),
    description: z.string().optional(),
    currencyId: z.number({ required_error: 'Currency is required' }).min(1, 'Currency is required'),
    bic: z.string().optional(),
    companyId: z.number({ required_error: 'Company is required' }).min(1, 'Company is required'),
    paymentsEnabled: z.boolean().default(false),
    connectivity: z.number().default(ConnectivityType.ManualUpload),
  })
  .refine((data) => Boolean(data.iban) || Boolean(data.accountNumber), {
    message: 'Either IBAN or Account Number is required',
    path: ['iban'],
  })
  .refine((data) => Boolean(data.iban) || Boolean(data.bic), {
    message: 'BIC is required when IBAN is not provided',
    path: ['bic'],
  })

export type AddAccountFormValues = z.infer<typeof addAccountSchema>

export const editAccountSchema = z
  .object({
    id: z.number(),
    accountName: z.string().min(1, 'Account name is required'),
    description: z.string().optional(),
    iban: z.string().optional(),
    accountNumber: z.string().optional(),
    companyId: z.coerce.number().min(1, 'Company is required'),
    // 0 = legacy unset, -1 = XXX placeholder (both valid at the schema level).
    // Editable accounts must pick a real currency, but non-editable accounts may load
    // with an unresolved id (legacy data) or the XXX placeholder while the field stays
    // disabled — the stricter "must be > 0" check for the editable case runs in onSubmit
    // instead of here.
    currencyId: z.coerce.number().min(-1),
    active: z.boolean().default(true),
  })
  .refine((data) => Boolean(data.iban) || Boolean(data.accountNumber), {
    message: 'Either IBAN or Account Number is required',
    path: ['iban'],
  })

export type EditAccountFormValues = z.infer<typeof editAccountSchema>

export const setBalanceSchema = z.object({
  accountId: z.number(),
  balanceStartDate: z.string().min(1, 'Date is required'),
  balance: z.coerce.number(),
})

export type SetBalanceFormValues = z.infer<typeof setBalanceSchema>
